<template>
  <div class="management-content">
    <div class="title-box">
      <div class="page-title">{{ $t("Header_License") }}</div>
      <div class="right-box">
        <!-- 필터 -->
        <a-popover class="search-filter" placement="bottomRight" trigger="click" v-model:visible="filterState.visible">
          <template #content>
            <div>
              <div class="filter-content">
                <div v-for="item in filterItems" :key="item.key" class="filter-item">
                  <div class="filter-title">{{ $t(item.label) }}</div>
                  <a-checkbox-group v-model:value="filterState[item.model]" :options="item.options"
                    class="check-group" />
                </div>
              </div>
              <hr />
              <div class="filter-button-box">
                <div>
                  <a-button class="filter-button reset" size="small" @click="onFilterReset">{{ $t('Common_FilterReset')
                    }}</a-button>
                </div>
                <div>
                  <a-button class="filter-button confirm" size="small" @click="onFilterConfirm">{{ $t('CH_password1')
                    }}</a-button>
                  <a-button class="filter-button cancel" size="small" @click="onFilterCancel">{{ $t('Common_Cancel')
                    }}</a-button>
                </div>
              </div>
            </div>
          </template>
          <a-button>
            {{ $t("Common_Filter") }}
            <div>
              <span v-if="filterCount" class="filter-count">{{ filterCount }}</span>
              <img v-else src="/img/common/down-arrow.png" />
            </div>
          </a-button>
        </a-popover>
        <!-- 검색 -->
        <a-input-search class="search-input" v-model:value="searchText" :placeholder="$t('Common_Search')"
          @search="onSearch" />
        <div class="page-buttons">
          <a-button class="menu-button" @click="toggleModal">{{ $t("Header_Issue") }}</a-button>
          <LicenseAddModal v-if="licenseVisible" :visible="licenseVisible" :toggleModal="toggleModal" />
        </div>
      </div>
    </div>
    <CommonTable :loading="loading" :data="tableData" :columns="columns" :pagination="pagination"
      :handleTableChange="handleTableChange" />
  </div>
</template>

<script>
import {
  computed,
  onUnmounted,
  onMounted,
  onUpdated,
  reactive,
  ref,
  toRefs,
  watch,
} from "vue";
import { useStore } from "vuex";
import axios from "axios";
import { apiUrl } from "@/utils/common";
import CommonTable from "@/components/commonComponents/CommonTable";
import LicenseAddModal from "@/components/sideComponents/LicenseAddModal";
import { useI18n } from "vue-i18n";

export default {
  name: "License",
  components: {
    CommonTable,
    LicenseAddModal,
  },
  setup() {
    const store = useStore();
    const licenseState = computed(() => store.getters["license/STATE"]);

    // 페이지를 떠날 때 필터 상태 초기화
    onUnmounted(() => {
      store.dispatch('license/CHANGE_FILTER', {});
    });

    // 실제 store에 반영된 필터 개수
    const filterCount = computed(() => {
      const filter = licenseState.value.param?.filter || {};
      let count = 0;
      Object.entries(filter).forEach(([key, value]) => {
        if (key === 'searchAll') return;
        if (Array.isArray(value)) {
          count += value.length;
        } else if (value) {
          count += 1;
        }
      });
      return count;
    });
    const { t } = useI18n();

    const state = reactive({
      tableData: [],
      loading: false,
      licenseVisible: false,
      searchText: '',
      pagination: {},
      pagesize: 12,
      currentPage: 1,
    });

    const columns = [
      {
        title: "No",
        dataIndex: "licenseNo",
        align: "center",
        width: 50,
        // sorter: true,
      },
      {
        title: t("LC_Col_Applicant"),
        dataIndex: "userName",
        align: "center",
        width: 55,
        ellipsis: true,
        // sorter: true,
      },
      {
        title: t("LC_Col_Client"),
        dataIndex: "customer",
        align: "center",
        width: 100,
        ellipsis: true,
        // sorter: true,
      },
      {
        title: t("Common_Content"),
        dataIndex: "licenseContent",
        width: 200,
        ellipsis: true,
        // sorter: true,
      },
      {
        title: t("LC_Col_LicenseType"),
        dataIndex: "licenseType",
        align: "center",
        width: 100,
        // sorter: true,
        slots: { customRender: "licenseType" },
      },
      {
        title: t("Common_TrainingNum"),
        dataIndex: "examLimitCount",
        align: "center",
        width: 70,
        // sorter: true,
      },
      {
        title: t("LC_Col_StartDate"),
        dataIndex: "startEpoch",
        align: "center",
        width: 70,
        // sorter: true,
        slots: { customRender: "date" },
      },
      {
        title: t("LC_Col_EndDate"),
        dataIndex: "endEpoch",
        align: "center",
        width: 70,
        // sorter: true,
        slots: { customRender: "date" },
      },
      {
        title: t("LC_Col_LicenseQuantity"),
        dataIndex: "licenseCount",
        align: "center",
        width: 70,
        // sorter: true,
        slots: { customRender: "persons" },
      },
      {
        title: t("LC_Col_IssueDate"),
        dataIndex: "regEpoch",
        align: "center",
        width: 100,
        // sorter: true,
        slots: { customRender: "dateTime" },
      },
    ];

    const LICENSE_TYPE_OPTIONS = [
      { label: t("LC_Modal_LicenseList1"), value: 1 },
      { label: t("LC_Modal_LicenseList2"), value: 2 },
      { label: t("LC_Modal_LicenseList3"), value: 4 },
    ];

    const filterState = reactive({
      visible: false,
      selectedLicenseTypes: [],
    });

    const filterItems = [
      {
        key: "licenseType",
        label: "LC_Col_LicenseType",
        model: "selectedLicenseTypes",
        options: LICENSE_TYPE_OPTIONS,
      },
    ];

    onMounted(() => {
      getLicense();
    });

    watch(
      () => licenseState.value.needReload,
      (current, prev) => {
        // 라이센스 발급 후 테이블 목록 재조회
        if (current) getLicense();
      }
    );

    watch(
      () => state.currentPage,
      () => {
        getLicense();
      }
    );

    // 페이지 변경 이벤트
    const handleTableChange = (pagination, filters, sorter) => {
      state.currentPage = pagination.current;
    };

    const toggleModal = () => {
      state.licenseVisible = !state.licenseVisible;
    };

    const getLicense = async () => {
      state.loading = true;
      try {
        // filter 동적 생성
        const filter = {};
        const filterKeys = [
          { key: 'licenseType', prop: 'licenseType' },
        ];
        const paramFilter = licenseState.value.param.filter || {};
        filterKeys.forEach(({ key, prop }) => {
          const arr = paramFilter[prop];
          if (Array.isArray(arr) && arr.length > 0) {
            filter[key] = arr;
          }
        });

        if (state.searchText) filter.searchAll = state.searchText;

        const params = {
          filter,
          sort: {},
          offset: state.pagesize * (state.currentPage - 1),
          limit: state.pagesize,
        };

        // const params = {
        //   filter: { ...licenseState.value.param.filter, ...(state.searchText ? { searchAll: state.searchText } : {}) },
        //   sort: {},
        //   offset: state["pageSize"] * (state["currentPage"] - 1),
        //   limit: state["pageSize"],
        // };

        const response = await axios.get(apiUrl + "License", { params });
        if (response && !response.data.error) {
          store.dispatch("license/GET_LICENSE", response);
          state.tableData = response.data.list;
          state.pagination.current = state.currentPage;
          state.pagination.pageSize = state.pagesize;
          state.pagination.total = licenseState.value.totalCount;
        }
      } catch (error) {
        console.log("get license error", error);
      }

      state.loading = false;
    };

    const onSearch = (value) => {
      // 1페이지로 초기화
      state.currentPage = 1;
      getLicense();
    }

    const onFilterConfirm = () => {
      filterState.visible = false;
      store.dispatch('license/CHANGE_FILTER', {
        ...licenseState.value.param.filter,
        licenseType: filterState.selectedLicenseTypes,
      });
      state.currentPage = 1;
      getLicense();
    };

    const onFilterCancel = () => {
      filterState.visible = false;
      filterState.selectedLicenseTypes = licenseState.value.param.filter.licenseType;
    };

    const onFilterReset = () => {
      filterState.selectedLicenseTypes = [];
      filterState.visible = false;
      store.dispatch('license/CHANGE_FILTER', {
        ...licenseState.value.param.filter,
        licenseType: [],
      });
      state.currentPage = 1;
      getLicense();
    };

    return {
      ...toRefs(state),
      columns,
      toggleModal,
      onSearch,
      handleTableChange,
      LICENSE_TYPE_OPTIONS,
      filterState,
      filterCount,
      filterItems,
      onFilterConfirm,
      onFilterCancel,
      onFilterReset,
    };
  },
};
</script>

<style></style>